import { theme } from 'antd';
import { DeviceGroupsIcon, DevicesIcon, InfoIcon, SearchIcon, UserIcon } from 'assets/images/svg';
import { groupPermissionsByTeam } from 'library/helpers/accessControl';
import { formatDisplayName } from 'library/helpers/utility';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectDevices } from 'reduxStore/devices/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectTeamPermissions } from 'reduxStore/permissions/selectors';
import { selectTeamsBySiteID } from 'reduxStore/teams/selectors';
import { PopoverTag, TableFilterDropdown } from 'zComponents/atoms';
import { DeleteTeamsModal, ManageMembersModal, TeamDrawer } from 'zComponents/molecules';
import { Button, Col, Empty, Flex, Input, Row, Typography, Table, Tooltip } from 'zui/Antd';
const { Text } = Typography;

const TeamsTab = ({ selectedSite }) => {
	// ✅ Hooks
	const {
		token: { colorTextDescription }
	} = theme.useToken();

	// ✅ Get data from Redux - site-specific teams
	const teams = useSelector((state) => selectTeamsBySiteID(state, selectedSite));
	const people = useSelector(selectPeople);
	const permissions = useSelector(selectTeamPermissions);
	const deviceGroups = useSelector(selectAssetGroups);
	const devices = useSelector(selectDevices);

	// ✅ Filters state
	const [searchText, setSearchText] = useState('');
	const [selectedTeamsIDs, setSelectedTeamsIDs] = useState([]);
	const [selectedTeam, setSelectedTeam] = useState(null);
	const [openDrawer, setOpenDrawer] = useState(false);
	const [inputValue, setInputValue] = useState('');

	// ✅ Debounced search handler
	const debouncedSearch = _.debounce((searchString) => {
		setSearchText(searchString);
	}, 300);

	// Apply filtering: only search by text (site filtering already done by selectTeamsBySiteID)
	const filteredTeams = useMemo(() => {
		return teams.filter((team) => {
			const matchesSearch =
				!searchText.length ||
				[team.name].some((field) => field?.toLowerCase().includes(searchText.toLowerCase()));
			return matchesSearch;
		});
	}, [teams, searchText]);

	const personNameById = useMemo(() => {
		const map = new Map();
		for (const p of people) map.set(p.personID, p.displayName || '');
		return map;
	}, [people]);

	// teams options
	const teamOptions = useMemo(() => {
		const uniqueTeamNames = [...new Set(teams.map((team) => team.name).filter(Boolean))];
		return uniqueTeamNames
			.map((teamName) => ({
				label: teamName,
				value: teamName
			}))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [teams]);

	// members options
	const membersOptions = useMemo(() => {
		const allMemberIDs = new Set();
		teams.forEach((team) => {
			team.members?.forEach((memberID) => allMemberIDs.add(memberID));
		});
		return Array.from(allMemberIDs)
			.map((memberID) => ({
				label: personNameById.get(memberID) || '',
				value: memberID
			}))
			.filter((option) => option.label) // Only include if we have a name
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [teams, personNameById]);

	// access permissions options
	const accessPermissionsOptions = useMemo(() => {
		const labels = new Set();

		teams.forEach((team) => {
			const teamPermissions = permissions.filter((p) => p.teamID === team.teamID);
			const displayItems = groupPermissionsByTeam({
				permissions: teamPermissions,
				devices,
				deviceGroups,
				teams: [team], // Pass current team to resolve name
				currentTeamID: team.teamID
			});

			displayItems.forEach((item) => labels.add(item.label));
		});

		return Array.from(labels)
			.map((label) => ({ label, value: label }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [teams, permissions, devices, deviceGroups]);

	// ✅ Handle input change
	const handleChangeInput = (e) => {
		const value = e.target.value;
		setInputValue(value);
		debouncedSearch(value);
	};

	// ✅ Table row selection
	const rowSelection = {
		selectedRowKeys: selectedTeamsIDs,
		onChange: setSelectedTeamsIDs
	};

	// 7) Table columns
	const columns = [
		{
			title: 'TEAM',
			dataIndex: 'name',
			key: 'name',
			render: (name) => {
				return (
					<Text strong style={{ lineHeight: '22px' }}>
						{name}
					</Text>
				);
			},
			sorter: (a, b) => a.name.localeCompare(b.name),
			onFilter: (value, record) => record.name === value,
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={teamOptions} placeholder='Search teams' />
			)
		},
		{
			title: 'MEMBERS',
			dataIndex: 'members',
			key: 'members',
			width: 200,
			render: (members) => {
				const memberData = members
					?.map((id) => {
						const person = people.find((p) => p.personID === id);
						return person ? { key: id, label: formatDisplayName(person)?.displayName } : null;
					})
					.filter(Boolean);

				const count = memberData?.length || 0;

				if (count === 0) return <Text type='secondary' style={{ fontSize: 12 }}>—</Text>;

				return (
					<PopoverTag
						icon={UserIcon}
						count={count}
						singular='member'
						plural='members'
						items={memberData}
					/>
				);
			},
			sorter: (a, b) => {
				const aMembers = a.members?.length || 0;
				const bMembers = b.members?.length || 0;
				// Primary sort: by number of members
				const lengthDiff = aMembers - bMembers;
				if (lengthDiff !== 0) return lengthDiff;
				// Secondary sort: by first member name alphabetically
				const aFirstMember = a.members?.[0] ? personNameById.get(a.members[0]) || '' : '';
				const bFirstMember = b.members?.[0] ? personNameById.get(b.members[0]) || '' : '';
				return aFirstMember.localeCompare(bFirstMember, undefined, { sensitivity: 'base' });
			},
			onFilter: (value, record) => {
				return record.members?.includes(value) || false;
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={membersOptions} placeholder='Search members' />
			)
		},
		{
			title: 'ACCESS PERMISSIONS',
			key: 'accessPermissions',
			width: 300,
			render: (_, record) => {
				const teamPermissions = permissions.filter((p) => p.teamID === record.teamID);
				const displayItems = groupPermissionsByTeam({
					permissions: teamPermissions,
					devices,
					deviceGroups,
					teams,
					currentTeamID: record.teamID
				});

				const directDevices = displayItems.filter((item) => !item.assetGroupID);
				const groupItems = displayItems.filter((item) => item.assetGroupID);

				if (directDevices.length === 0 && groupItems.length === 0) return <Text type='secondary' style={{ fontSize: 12 }}>—</Text>;

				return (
					<Flex gap={4} wrap='wrap'>
						{directDevices.length > 0 && (
							<PopoverTag
								icon={DevicesIcon}
								count={directDevices.length}
								singular='device'
								plural='devices'
								items={directDevices.map((item, i) => ({ key: i, label: item.label }))}
							/>
						)}
						{groupItems.length > 0 && (
							<PopoverTag
								icon={DeviceGroupsIcon}
								count={groupItems.length}
								singular='group'
								plural='groups'
								items={groupItems.map((item, i) => ({ key: i, label: item.label }))}
							/>
						)}
					</Flex>
				);
			},
			onFilter: (value, record) => {
				const teamPermissions = permissions.filter((p) => p.teamID === record.teamID);
				const displayItems = groupPermissionsByTeam({
					permissions: teamPermissions,
					devices,
					deviceGroups,
					teams,
					currentTeamID: record.teamID
				});
				return displayItems.some((item) => item.label === value);
			},
			sorter: (a, b) => {
				const aCount = permissions.filter((p) => p.teamID === a.teamID).length;
				const bCount = permissions.filter((p) => p.teamID === b.teamID).length;
				return aCount - bCount;
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={accessPermissionsOptions} placeholder='Search permissions' />
			)
		},
		{
			title: 'ACTIONS',
			dataIndex: 'actions',
			align: 'center',
			width: 150,
			fixed: 'right',
			render: (_, record) => (
				<Flex gap={8} justify='center' onClick={(e) => e.stopPropagation()}>
					<Tooltip title='View Team Details' placement='top'>
						<Button
							type='text'
							icon={<InfoIcon color='currentColor' />}
							size='small'
							onClick={() => {
								setSelectedTeam(record.teamID);
								setOpenDrawer(true);
							}}
						/>
					</Tooltip>

					<ManageMembersModal teamID={record.teamID} />
					<DeleteTeamsModal teamIDs={[record.teamID]} />
				</Flex>
			)
		}
	];

	return (
		<Row>
			<Col span={24} style={{ width: '100%', marginBottom: 24 }}>
				<Row justify='space-between'>
					<Col span={10}>
						<Input
							placeholder='Search Teams'
							allowClear
							value={inputValue}
							onChange={(e) => handleChangeInput(e)}
							onClear={() => setInputValue('')}
							suffix={<SearchIcon color={colorTextDescription} />}
						/>
					</Col>
				</Row>
			</Col>
			<Col span={24}>
				<Table
					rowSelection={rowSelection}
					dataSource={filteredTeams.map((team) => ({ ...team, key: team.teamID }))}
					columns={columns}
					size='small'
					locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No teams found in this site.' /> }}
					onRow={(record) => ({
						onClick: () => {
							setSelectedTeam(record.teamID);
							setOpenDrawer(true);
						},
						style: { cursor: 'pointer', height: '61px' }
					})}
					pagination={{
						defaultPageSize: 10,
						showQuickJumper: true,
						showSizeChanger: false
					}}
					scroll={{ x: 1500 }}
				/>
			</Col>

			<TeamDrawer teamID={selectedTeam} visible={openDrawer} setVisible={setOpenDrawer} />
		</Row>
	);
};

export default TeamsTab;
